import {
  ArrowDownToLine,
  Bot,
  ChevronDown,
  ChevronRight,
  Gem,
  HeartPulse,
  LogIn,
  Pickaxe,
  RefreshCw,
  Shield,
  Sparkles,
  Swords,
  Trophy,
  Users,
  WandSparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { APIClient } from "../api";
import { LadderPanel } from "../LadderPanel";
import { RankingPanel } from "./RankingPanel";
import type { SocialProfile } from "../types";
import {
  type GrowthProfile,
  type InventoryView,
  type MeResponse,
  type OfflineAutomationSettings,
  type OfflineAutomationSkill,
  type OfflineQuotaSource,
  type RoomListing,
} from "../types";
import { EMPTY_PROGRESSION } from "../ui/defaults";
import { collectCombatBonuses, formatOfflineDuration, formatRecoveryDuration } from "../ui/format";
import { PageSections } from "../ui/PageSections";
import { Meter } from "../ui/Resources";

export function DiveScreen({
  api,
  social = null,
  me,
  inventory,
  growth,
  busy,
  onStart,
  onHeal,
  onClaimOffline,
  onPurchaseOffline,
  onOfflineSettings,
  onMatchRaid,
  onSoloRaid,
  onCreateDuo,
  onJoinRoom,
  onListRooms,
  onStartRace,
}: {
  api: APIClient;
  social?: SocialProfile | null;
  me: MeResponse;
  inventory: InventoryView;
  growth: GrowthProfile;
  busy: boolean;
  onStart: (shaftDepth: number, useFreeSkip: boolean) => void;
  onHeal: () => void;
  onClaimOffline: () => void;
  onPurchaseOffline: (packageId: string) => void;
  onOfflineSettings: (source: OfflineQuotaSource, automation?: OfflineAutomationSettings) => void;
  onMatchRaid: () => void;
  onSoloRaid: () => void;
  onCreateDuo: () => void;
  onJoinRoom: (code: string) => void;
  onStartRace: (kind: "daily" | "weekly") => void;
  onListRooms: (mode: "raid" | "duo" | "") => Promise<RoomListing[]>;
}) {
  const [clock, setClock] = useState(Date.now());
  const [pageTab, setPageTab] = useState("battle");
  useEffect(() => {
    setClock(Date.now());
    const timer = window.setInterval(() => setClock(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [me.player.hpEvaluatedAt, me.player.hpFullAt]);
  const recoveryEstimate = useMemo(() => {
    const available =
      typeof me.player.hpEvaluatedAt === "number" && typeof me.player.hpFullAt === "number";
    return {
      available,
      startedAt: Date.now(),
      duration: available ? Math.max(0, me.player.hpFullAt! - me.player.hpEvaluatedAt!) : 0,
    };
  }, [me.player.hpEvaluatedAt, me.player.hpFullAt, me.player.hpRatio]);
  const recoveryElapsed = Math.max(0, clock - recoveryEstimate.startedAt);
  const recoveryProgress =
    recoveryEstimate.available && recoveryEstimate.duration > 0
      ? Math.max(0, Math.min(1, recoveryElapsed / recoveryEstimate.duration))
      : 0;
  const hpRatio = Math.max(
    0,
    Math.min(1, me.player.hpRatio + (1 - me.player.hpRatio) * recoveryProgress),
  );
  const hp = Math.round(hpRatio * me.player.maxHp);
  const recoveryLabel =
    hpRatio >= 1
      ? "生命已回满"
      : recoveryEstimate.available
        ? `预计 ${formatRecoveryDuration(recoveryEstimate.duration - recoveryElapsed)}后回满`
        : "正在计算恢复时间";
  const healing = me.player.diamondHealing;
  const healingPercent = Math.round(healing.restoreRatio * 100);
  const healingExhausted = healing.usesToday >= healing.maxDailyUses;
  const healingDisabled =
    busy || hpRatio >= 1 || healingExhausted || me.player.diamond < healing.nextCost;
  const combatBonuses = collectCombatBonuses(inventory.stats);
  const progression = me.progression ?? inventory.progression ?? EMPTY_PROGRESSION;
  const levelPercent =
    progression.nextLevelExperience > 0
      ? (progression.currentLevelExperience / progression.nextLevelExperience) * 100
      : 100;
  const shafts = growth.unlockedShafts?.length ? growth.unlockedShafts : [1];
  const [shaftDepth, setShaftDepth] = useState(shafts[shafts.length - 1]);
  const [useFreeSkip, setUseFreeSkip] = useState(growth.effects.freeSkipLevels > 0);
  const [inviteCode, setInviteCode] = useState("");
  const [roomListings, setRoomListings] = useState<RoomListing[]>([]);
  const [roomFilter, setRoomFilter] = useState<"raid" | "duo" | "">("");
  const [offlineDialogOpen, setOfflineDialogOpen] = useState(false);
  const [battleMode, setBattleMode] = useState("solo");
  const portalLevel = growth.buildings.find((building) => building.id === "portal")?.level ?? 0;
  const raidRewardBonus = Math.max(0, Math.round((growth.effects.raidRewardMultiplier - 1) * 100));
  const raidAttempts = me.player.raidAttempts ?? { remaining: 0, max: 0 };
  const raidUnavailable = raidAttempts.remaining <= 0;
  const offline = me.player.offline;
  const offlineAutomation = offline?.quota.automationPolicy;
  const offlineSkills = Array.isArray(offlineAutomation?.skills) ? offlineAutomation.skills : [];
  const toggleOfflineSkill = (skillId: string) => {
    if (!offline || !offlineAutomation) return;
    const configured = new Map(offlineSkills.map((skill) => [skill.skillId, skill.enabled]));
    if (offlineSkills.length === 0) growth.skillSlots.forEach((id) => configured.set(id, true));
    configured.set(skillId, !(configured.get(skillId) ?? true));
    onOfflineSettings(offline.quota.preferredSource, {
      ...offlineAutomation,
      skills: growth.skillSlots.map((id, priority) => ({
        skillId: id,
        enabled: configured.get(id) ?? true,
        priority,
      })),
    });
  };
  const updateOfflineSkill = (
    skillId: string,
    patch: Partial<OfflineAutomationSettings["skills"][number]>,
  ) => {
    if (!offline || !offlineAutomation) return;
    const skills = growth.skillSlots.map((id, priority) => {
      const current = offlineSkills.find((skill) => skill.skillId === id) ?? {
        skillId: id,
        enabled: true,
        priority,
      };
      return id === skillId ? { ...current, priority, ...patch } : { ...current, priority };
    });
    onOfflineSettings(offline.quota.preferredSource, { ...offlineAutomation, skills });
  };
  useEffect(() => {
    if (!offlineDialogOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOfflineDialogOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [offlineDialogOpen]);
  return (
    <section className="dive-screen" aria-labelledby="dive-title">
      <header className="hub-page-header">
        <div>
          <h1 id="dive-title">战斗</h1>
        </div>
        <span className="depth-record">
          最深记录 <strong>{growth.bestDepth}</strong> 层
        </span>
      </header>
      <PageSections
        label="战斗分页"
        value={pageTab}
        onChange={setPageTab}
        items={[
          { id: "battle", label: "战斗", icon: <Swords size={17} /> },
          { id: "ranking", label: "排行", icon: <Trophy size={17} /> },
        ]}
      />
      {pageTab === "ranking" ? (
        <RankingPanel profile={social} />
      ) : (
        <>
          <LadderPanel api={api} growth={growth} onChanged={async () => undefined} />
          {portalLevel > 0 && (
            <PageSections
              label="战斗模式"
              value={battleMode}
              onChange={setBattleMode}
              items={[
                { id: "solo", label: "单人下潜", icon: <Swords size={17} /> },
                { id: "party", label: "传送阵", icon: <Users size={17} /> },
              ]}
            />
          )}
          <div className="dive-status-band">
            <div className="hp-readout">
              <div className="readout-label">
                <span>生命</span>
                <strong>
                  {hp} / {me.player.maxHp}
                </strong>
              </div>
              <Meter value={hpRatio * 100} tone="hp" />
              <div className="hp-recovery-row">
                <small className="hp-recovery">{recoveryLabel}</small>
                <span className="diamond-balance">
                  <Gem size={13} />
                  {me.player.diamond}
                </span>
                <button
                  className="secondary-command diamond-heal-command"
                  disabled={healingDisabled}
                  onClick={onHeal}
                  title={`使用钻石回复最大生命值的 ${healingPercent}%`}
                  aria-label={`回复 ${healingPercent}% 生命，今日已使用 ${healing.usesToday} 次，共 ${healing.maxDailyUses} 次`}
                >
                  <HeartPulse size={14} />
                  <span>
                    {hpRatio >= 1
                      ? "生命已满"
                      : healingExhausted
                        ? "已达上限"
                        : `回复 ${healingPercent}%`}
                  </span>
                  {!healingExhausted && <strong>{healing.nextCost}钻</strong>}
                  <small>
                    今日 {healing.usesToday}/{healing.maxDailyUses}
                  </small>
                </button>
              </div>
            </div>
            <div className="growth-readout">
              <div className="readout-label">
                <span>人物等级</span>
                <strong>Lv.{progression.level}</strong>
              </div>
              <Meter value={levelPercent} tone="xp" />
            </div>
          </div>
          {battleMode === "solo" && (
            <>
              <footer className="dive-action-band">
                <div className="shaft-controls">
                  <label>
                    <span>竖井起点</span>
                    <select
                      value={shaftDepth}
                      onChange={(event) => setShaftDepth(Number(event.target.value))}
                    >
                      {shafts.map((depth) => (
                        <option key={depth} value={depth}>
                          {depth === 1 ? "入口 · 第 1 层" : `${depth} 层竖井 · 第 ${depth + 1} 层`}
                        </option>
                      ))}
                    </select>
                  </label>
                  {growth.effects.freeSkipLevels > 0 && (
                    <label className="skip-toggle">
                      <input
                        type="checkbox"
                        checked={useFreeSkip}
                        onChange={(event) => setUseFreeSkip(event.target.checked)}
                      />
                      <span>免费跳过 {growth.effects.freeSkipLevels} 层</span>
                    </label>
                  )}
                </div>
                <button
                  className="primary-command"
                  disabled={busy || hpRatio < 0.05}
                  onClick={() => onStart(shaftDepth, useFreeSkip)}
                >
                  <ArrowDownToLine size={20} />
                  {hpRatio < 0.05 ? "等待生命恢复" : "开始战斗"}
                </button>
              </footer>
              <details className="combat-preparation">
                <summary>
                  <Shield size={17} />
                  战斗属性与加成
                  <ChevronDown size={16} />
                </summary>
                <div className="dive-combat-stats" aria-label="战斗属性">
                  <span>
                    <Pickaxe size={17} />
                    <small>攻击</small>
                    <strong>{inventory.stats.attack}</strong>
                  </span>
                  <span>
                    <Shield size={17} />
                    <small>防御</small>
                    <strong>{inventory.stats.defense}</strong>
                  </span>
                  <span>
                    <HeartPulse size={17} />
                    <small>生命</small>
                    <strong>{inventory.stats.maxHp}</strong>
                  </span>
                  <span>
                    <WandSparkles size={17} />
                    <small>法力</small>
                    <strong>{inventory.stats.manaCap}</strong>
                  </span>
                </div>
                {combatBonuses.length > 0 && (
                  <section className="dive-bonuses" aria-labelledby="dive-bonuses-title">
                    <header>
                      <Sparkles size={15} />
                      <strong id="dive-bonuses-title">当前加成</strong>
                    </header>
                    <dl>
                      {combatBonuses.map((bonus) => (
                        <div key={bonus.label}>
                          <dt>{bonus.label}</dt>
                          <dd>{bonus.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                )}
              </details>
            </>
          )}
          {portalLevel > 0 && battleMode === "party" && (
            <section className="multiplayer-entry" aria-label="多人玩法">
              <header>
                <Users size={18} />
                <strong>多人传送阵</strong>
                <span>
                  Raid {raidAttempts.remaining}/{raidAttempts.max} · 奖励 +{raidRewardBonus}% · Lv.
                  {portalLevel}
                </span>
              </header>
              <div className="multiplayer-actions">
                <button
                  className="secondary-command compact"
                  disabled={busy || raidUnavailable}
                  onClick={onMatchRaid}
                >
                  <Users size={16} />
                  {raidUnavailable ? "本周次数已用尽" : "Raid 匹配"}
                </button>
                <button
                  className="secondary-command compact"
                  disabled={busy || raidUnavailable}
                  onClick={onSoloRaid}
                >
                  <Swords size={16} />
                  {raidUnavailable ? "本周次数已用尽" : "单人 Raid"}
                </button>
                {portalLevel >= 3 && (
                  <button
                    className="secondary-command compact"
                    disabled={busy}
                    onClick={onCreateDuo}
                  >
                    <Users size={16} />
                    创建协作矿道
                  </button>
                )}
                {portalLevel >= 5 && (
                  <>
                    <button
                      className="secondary-command compact"
                      disabled={busy}
                      onClick={() => onStartRace("daily")}
                    >
                      <Trophy size={16} />
                      每日竞速
                    </button>
                    <button
                      className="secondary-command compact"
                      disabled={busy}
                      onClick={() => onStartRace("weekly")}
                    >
                      <Trophy size={16} />
                      每周竞速
                    </button>
                  </>
                )}
              </div>
              <form
                className="room-code-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (inviteCode.trim()) onJoinRoom(inviteCode.trim());
                }}
              >
                <input
                  value={inviteCode}
                  onChange={(event) => setInviteCode(event.target.value.toUpperCase())}
                  placeholder="房间码"
                  maxLength={12}
                />
                <button
                  className="icon-button"
                  disabled={busy || !inviteCode.trim()}
                  aria-label="加入房间"
                  title="加入房间"
                >
                  <LogIn size={17} />
                </button>
              </form>
              <div className="room-browser">
                <div className="room-browser-controls">
                  <select
                    value={roomFilter}
                    onChange={(event) =>
                      setRoomFilter(event.currentTarget.value as "raid" | "duo" | "")
                    }
                  >
                    <option value="">全部任务</option>
                    <option value="raid">Raid</option>
                    <option value="duo">协作矿道</option>
                  </select>
                  <button
                    className="icon-button"
                    disabled={busy}
                    onClick={() => void onListRooms(roomFilter).then(setRoomListings)}
                    title="刷新公开房间"
                    aria-label="刷新公开房间"
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>
                {roomListings.map((listing) => (
                  <button
                    className="room-listing"
                    key={listing.id}
                    disabled={busy}
                    onClick={() => onJoinRoom(listing.inviteCode)}
                  >
                    <span>
                      <strong>{listing.mode === "raid" ? "Raid" : "矿道"}</strong>
                      <small>
                        {listing.ownerName} · 最深 {listing.ownerDepth}
                      </small>
                    </span>
                    <b>
                      {listing.players}/{listing.maxPlayers}
                    </b>
                  </button>
                ))}
              </div>
            </section>
          )}
          {offline && battleMode === "solo" && (
            <section className="offline-panel-trigger" aria-label="离线战斗">
              <button
                type="button"
                onClick={() => setOfflineDialogOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={offlineDialogOpen}
              >
                <Bot size={18} />
                <span>
                  <strong>离线战斗</strong>
                  <small>
                    {offline.quota.preferredSource === "free" ? "使用免费额度" : "使用付费额度"}
                  </small>
                </span>
                <span className="offline-trigger-balances">
                  <b>{formatOfflineDuration(offline.quota.freeSeconds)}</b>
                  <small>免费</small>
                </span>
                <span className="offline-trigger-balances">
                  <b>{formatOfflineDuration(offline.quota.paidSeconds)}</b>
                  <small>付费</small>
                </span>
                <ChevronRight size={17} />
              </button>
            </section>
          )}
          {offline && offlineDialogOpen && (
            <div
              className="modal-backdrop offline-modal-backdrop"
              role="presentation"
              onMouseDown={(event) =>
                event.target === event.currentTarget && setOfflineDialogOpen(false)
              }
            >
              <section
                className="offline-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="offline-dialog-title"
              >
                <header className="offline-dialog-header">
                  <div>
                    <Bot size={19} />
                    <h2 id="offline-dialog-title">离线战斗</h2>
                  </div>
                  <button
                    className="icon-button"
                    autoFocus
                    onClick={() => setOfflineDialogOpen(false)}
                    aria-label="关闭离线战斗"
                    title="关闭离线战斗"
                  >
                    <X size={18} />
                  </button>
                </header>
                <div className="offline-dialog-body">
                  <section className="offline-quota-section">
                    <header>
                      <strong>战斗额度</strong>
                      <button
                        className="secondary-command compact"
                        disabled={busy || !offline.quota.claimAvailable}
                        onClick={onClaimOffline}
                      >
                        <ArrowDownToLine size={14} />
                        {offline.quota.claimAvailable ? "领取 2 小时" : "本期已领取"}
                      </button>
                    </header>
                    <div className="offline-quota-balances">
                      <span>
                        <small>免费额度</small>
                        <strong>{formatOfflineDuration(offline.quota.freeSeconds)}</strong>
                      </span>
                      <span>
                        <small>付费额度</small>
                        <strong>{formatOfflineDuration(offline.quota.paidSeconds)}</strong>
                      </span>
                    </div>
                    <div
                      className="offline-source-control"
                      role="group"
                      aria-label="断线后使用的战斗额度"
                    >
                      <button
                        className={offline.quota.preferredSource === "free" ? "is-active" : ""}
                        disabled={busy}
                        onClick={() => onOfflineSettings("free")}
                      >
                        免费额度
                      </button>
                      <button
                        className={offline.quota.preferredSource === "paid" ? "is-active" : ""}
                        disabled={busy}
                        onClick={() => onOfflineSettings("paid")}
                      >
                        付费额度
                      </button>
                    </div>
                  </section>
                  {offlineAutomation && growth.skillSlots.length > 0 && (
                    <section className="offline-policy-section">
                      <header>
                        <strong>自动技能</strong>
                      </header>
                      <div className="offline-skill-policy" aria-label="离线自动技能">
                        {growth.skillSlots.map((skillId, priority) => {
                          const configured = offlineSkills.find(
                            (skill) => skill.skillId === skillId,
                          );
                          const skill = growth.skills.find((entry) => entry.id === skillId);
                          const condition = configured?.condition ?? "";
                          return (
                            <label key={skillId}>
                              <input
                                type="checkbox"
                                checked={configured?.enabled ?? true}
                                disabled={busy}
                                onChange={() => toggleOfflineSkill(skillId)}
                              />
                              <span>{priority + 1}</span>
                              <strong>{skill?.name ?? skillId}</strong>
                              <select
                                value={condition}
                                disabled={busy || !(configured?.enabled ?? true)}
                                onChange={(event) =>
                                  updateOfflineSkill(skillId, {
                                    condition: event.target
                                      .value as OfflineAutomationSkill["condition"],
                                    threshold:
                                      event.target.value === "" || event.target.value === "always"
                                        ? 0.5
                                        : (configured?.threshold ?? 0.5),
                                  })
                                }
                              >
                                <option value="">默认条件</option>
                                <option value="always">总是释放</option>
                                <option value="hp_below">生命低于阈值</option>
                                <option value="mana_above">法力高于阈值</option>
                                <option value="block_below">护盾低于数值</option>
                                <option value="enemy_hp_below">敌人生命低于阈值</option>
                              </select>
                              {(condition === "hp_below" ||
                                condition === "mana_above" ||
                                condition === "enemy_hp_below") && (
                                <input
                                  className="offline-threshold"
                                  type="number"
                                  min="0.05"
                                  max="0.95"
                                  step="0.05"
                                  value={configured?.threshold ?? 0.5}
                                  disabled={busy}
                                  onChange={(event) =>
                                    updateOfflineSkill(skillId, {
                                      condition,
                                      threshold: Number(event.target.value),
                                    })
                                  }
                                />
                              )}
                              {condition === "block_below" && (
                                <input
                                  className="offline-threshold"
                                  type="number"
                                  min="0"
                                  step="5"
                                  value={configured?.threshold ?? 0}
                                  disabled={busy}
                                  onChange={(event) =>
                                    updateOfflineSkill(skillId, {
                                      condition,
                                      threshold: Number(event.target.value),
                                    })
                                  }
                                />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </section>
                  )}
                  {offlineAutomation && (
                    <section className="offline-policy-section">
                      <header>
                        <strong>离线暂停</strong>
                      </header>
                      <div className="offline-retreat-policy">
                        <label>
                          <span>生命低于</span>
                          <select
                            value={offlineAutomation.retreatHpRatio ?? 0}
                            disabled={busy}
                            onChange={(event) =>
                              onOfflineSettings(offline.quota.preferredSource, {
                                ...offlineAutomation,
                                retreatHpRatio: Number(event.target.value),
                              })
                            }
                          >
                            <option value={0}>不自动暂停</option>
                            <option value={0.5}>50%</option>
                            <option value={0.4}>40%</option>
                            <option value={0.3}>30%</option>
                            <option value={0.2}>20%</option>
                          </select>
                        </label>
                        <label>
                          <span>到达层数</span>
                          <input
                            type="number"
                            min="0"
                            step="10"
                            value={offlineAutomation.retreatDepth ?? 0}
                            disabled={busy}
                            onChange={(event) =>
                              onOfflineSettings(offline.quota.preferredSource, {
                                ...offlineAutomation,
                                retreatDepth: Math.max(0, Number(event.target.value)),
                              })
                            }
                          />
                        </label>
                      </div>
                    </section>
                  )}
                  <section className="offline-policy-section">
                    <header>
                      <strong>补充额度</strong>
                      <span>
                        <Gem size={13} />
                        {me.player.diamond}
                      </span>
                    </header>
                    <div className="offline-packages" aria-label="购买离线战斗额度">
                      {offline.packages.map((item) => (
                        <button
                          key={item.id}
                          className="secondary-command compact"
                          disabled={busy || me.player.diamond < item.diamond}
                          onClick={() => onPurchaseOffline(item.id)}
                        >
                          <Bot size={14} />
                          <span>{formatOfflineDuration(item.seconds)}</span>
                          <strong>{item.diamond}钻</strong>
                        </button>
                      ))}
                    </div>
                  </section>
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </section>
  );
}
